import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  adminAlertsApi,
  adminMessagesApi,
  adminClientsApi,
  adminSuggestionsApi,
  adminAppointmentsApi,
} from '@/api';
import { useBusinessStore } from '@/stores';
// Direct import to avoid circular dependency through the components barrel
import { useToast } from '../AppToast';
import type { BusinessAlert } from '@tilly/shared';
import type {
  AppointmentDTO,
  ConversationDTO,
  UnmatchedMessageDTO,
  ClientListDTO,
  MessageSuggestionDTO,
} from '@tilly/shared';
import type { FeedItem } from './types';

// ---------------------------------------------------------------------------
// Alert → FeedItem mapping helpers
// ---------------------------------------------------------------------------

function getAlertTitle(
  type: BusinessAlert['type'],
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  switch (type) {
    case 'slow_day':
      return t('admin:assistantFeed.alertTitles.slow_day');
    case 'churn_risk':
      return t('admin:assistantFeed.alertTitles.churn_risk');
    default:
      return t('admin:assistantFeed.alertTitles.default');
  }
}

function getAlertDescription(
  type: BusinessAlert['type'],
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  switch (type) {
    case 'slow_day':
      return t('admin:assistantFeed.alertDescriptions.slow_day');
    case 'churn_risk':
      return t('admin:assistantFeed.alertDescriptions.churn_risk');
    default:
      return t('admin:assistantFeed.alertDescriptions.default');
  }
}

function getAlertCtaLabel(
  type: BusinessAlert['type'],
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  switch (type) {
    case 'slow_day':
      return t('admin:assistantFeed.alertCtas.slow_day');
    case 'churn_risk':
      return t('admin:assistantFeed.alertCtas.churn_risk');
    default:
      return t('admin:assistantFeed.card.view');
  }
}

function getAlertCtaPath(type: BusinessAlert['type']): string {
  switch (type) {
    case 'slow_day':
      return '/admin/messages';
    case 'churn_risk':
      return '/admin/messages';
    default:
      return '/admin/dashboard';
  }
}

// ---------------------------------------------------------------------------
// Conversation → FeedItem mapping
// A conversation surfaces when:
//   - it has unread inbound messages, OR
//   - the last message was inbound (client sent a message with no reply yet)
// ---------------------------------------------------------------------------

function needsReply(conv: ConversationDTO): boolean {
  if (conv.unreadCount > 0) return true;
  return conv.lastMessage?.direction === 'inbound';
}

function mapConversationToFeedItem(
  conv: ConversationDTO,
  navigate: (path: string) => void,
  t: (key: string, options?: Record<string, unknown>) => string
): FeedItem {
  const hasUnread = conv.unreadCount > 0;
  return {
    id: `conv-${conv.clientId}`,
    source: 'message',
    type: 'unread_message',
    severity: conv.unreadCount >= 3 ? 'warning' : 'info',
    title: conv.clientName,
    description: conv.lastMessage?.body ?? t('admin:assistantFeed.conversation.newMessageReceived'),
    timestamp: conv.lastMessage?.timestamp ?? Date.now(),
    ctaLabel: hasUnread
      ? t('admin:assistantFeed.conversation.replyUnread', { count: conv.unreadCount })
      : t('admin:assistantFeed.conversation.reply'),
    ctaAction: () => navigate('/admin/messages'),
    raw: conv,
  };
}

// ---------------------------------------------------------------------------
// Unmatched message → FeedItem mapping
// ---------------------------------------------------------------------------

function mapUnmatchedToFeedItem(
  msg: UnmatchedMessageDTO,
  navigate: (path: string) => void,
  t: (key: string, options?: Record<string, unknown>) => string
): FeedItem {
  const from = msg.fromPhone ?? msg.fromEmail ?? t('admin:assistantFeed.unmatched.unknownSender');
  return {
    id: `unmatched-${msg.id}`,
    source: 'unmatched',
    type: 'unmatched_message',
    severity: 'warning',
    title: t('admin:assistantFeed.unmatched.unknownContact'),
    description: `${from}: ${msg.body}`,
    timestamp: msg.timestamp,
    ctaLabel: t('admin:assistantFeed.unmatched.cta'),
    ctaAction: () => navigate('/admin/messages'),
    raw: msg,
  };
}

// ---------------------------------------------------------------------------
// Lapsed client → FeedItem mapping
// ---------------------------------------------------------------------------

function mapLapsedClientToFeedItem(
  client: ClientListDTO,
  navigate: (path: string) => void,
  t: (key: string, options?: Record<string, unknown>) => string
): FeedItem {
  const daysSince = client.visitCadence?.daysSinceLastVisit;
  const description =
    daysSince != null
      ? t('admin:assistantFeed.lapsed.descriptionWithDays', { count: daysSince })
      : t('admin:assistantFeed.lapsed.descriptionNoRecentVisits');

  // Use lastVisit as timestamp if available, otherwise place at end of sort
  const lastVisitMs = client.lastVisit ? new Date(client.lastVisit).getTime() : 0;

  return {
    id: `lapsed-${client.id}`,
    source: 'alert',
    type: 'lapsed_client',
    severity: 'info',
    title: t('admin:assistantFeed.lapsed.title', { name: client.name }),
    description,
    timestamp: lastVisitMs,
    ctaLabel: t('admin:assistantFeed.lapsed.cta'),
    ctaAction: () => navigate('/admin/messages'),
    raw: client,
  };
}

// ---------------------------------------------------------------------------
// MessageSuggestion → FeedItem mapping
// ---------------------------------------------------------------------------

function mapSuggestionToFeedItem(
  suggestion: MessageSuggestionDTO,
  onOpenModal: (s: MessageSuggestionDTO) => void,
  onReject: (id: string) => Promise<void>,
  t: (key: string, options?: Record<string, unknown>) => string
): FeedItem {
  return {
    id: `suggestion-${suggestion.id}`,
    source: 'suggestion',
    type: 'suggested_message',
    severity: 'info',
    title: suggestion.clientName,
    description: suggestion.body.slice(0, 120),
    timestamp: suggestion.createdAt,
    ctaLabel: t('admin:suggestions.review'),
    ctaAction: () => onOpenModal(suggestion),
    dismissAction: () => onReject(suggestion.id),
    raw: suggestion,
  };
}

function mapUnconfirmedAppointmentToFeedItem(
  appointment: AppointmentDTO,
  navigate: (path: string) => void,
  t: (key: string, options?: Record<string, unknown>) => string,
  onRequestConfirmation: (appointmentId: string) => void
): FeedItem {
  const smsSent = !!appointment.confirmationRequestedAt;
  const timeStr = dayjs(appointment.startTime).format('MMM D, h:mm A');
  return {
    id: `unconfirmed-${appointment.id}`,
    source: 'alert',
    type: 'unconfirmed_appointment',
    severity: 'warning',
    title: t('admin:assistantFeed.unconfirmed.title', { name: appointment.clientName }),
    description: smsSent
      ? t('admin:assistantFeed.unconfirmed.descriptionSmsSent', { time: timeStr })
      : t('admin:assistantFeed.unconfirmed.description', { time: timeStr }),
    timestamp: appointment.confirmationRequestedAt ?? new Date(appointment.startTime).getTime(),
    ctaLabel: smsSent
      ? t('admin:assistantFeed.unconfirmed.ctaSmsSent')
      : t('admin:assistantFeed.unconfirmed.cta'),
    ctaAction: smsSent
      ? () => navigate('/admin/calendar')
      : () => onRequestConfirmation(appointment.id),
    raw: appointment,
  };
}

function getFeedItemPriority(item: FeedItem): number {
  switch (item.type) {
    case 'unread_message':
      return 5;
    case 'suggested_message':
      return 4;
    case 'unconfirmed_appointment':
      return 3;
    case 'churn_risk':
      return 2;
    case 'lapsed_client':
      return 1;
    case 'slow_day':
      return 0;
    default:
      return 0;
  }
}

function getFeedDedupKey(item: FeedItem): string {
  if (item.type === 'slow_day') return 'business:slow_day';
  if (item.type === 'unmatched_message') return `unmatched:${item.id}`;

  const raw = item.raw as { clientId?: string; id?: string; subjectId?: string } | null | undefined;
  const clientId = raw?.clientId ?? raw?.subjectId ?? raw?.id;

  if (clientId) return `client:${clientId}`;
  return `item:${item.id}`;
}

function dedupeFeedItems(items: FeedItem[]): FeedItem[] {
  const deduped = new Map<string, FeedItem>();

  for (const item of items) {
    const key = getFeedDedupKey(item);
    const existing = deduped.get(key);

    if (!existing) {
      deduped.set(key, item);
      continue;
    }

    const existingPriority = getFeedItemPriority(existing);
    const nextPriority = getFeedItemPriority(item);

    if (nextPriority > existingPriority) {
      deduped.set(key, item);
      continue;
    }

    if (nextPriority === existingPriority && item.timestamp > existing.timestamp) {
      deduped.set(key, item);
    }
  }

  return Array.from(deduped.values()).sort((a, b) => b.timestamp - a.timestamp);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAssistantFeed() {
  const { t: rawT } = useTranslation(['admin']);
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();

  const t = (key: string, options?: Record<string, unknown>) =>
    rawT(key as never, options as never) as unknown as string;

  const alertsQuery = useQuery({
    queryKey: ['business-alerts', activeBusinessId],
    queryFn: () => adminAlertsApi.getAlerts(activeBusinessId!),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const conversationsQuery = useQuery({
    queryKey: ['conversations', activeBusinessId],
    queryFn: () => adminMessagesApi.getConversations(activeBusinessId!),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const unmatchedQuery = useQuery({
    queryKey: ['unmatched-messages', activeBusinessId],
    queryFn: () => adminMessagesApi.getUnmatched(activeBusinessId!),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const lapsedClientsQuery = useQuery({
    queryKey: ['clients-lapsed', activeBusinessId],
    queryFn: () =>
      adminClientsApi.getClients(
        activeBusinessId!,
        undefined, // search
        50, // limit
        0, // offset
        undefined, // tagIds
        ['lapsed'] // segments filter
      ),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const suggestionsQuery = useQuery({
    queryKey: ['suggestions', activeBusinessId],
    queryFn: () => adminSuggestionsApi.getSuggestions(activeBusinessId!, 'pending'),
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const unconfirmedAppointmentsQuery = useQuery({
    queryKey: ['appointments-unconfirmed', activeBusinessId],
    queryFn: async () => {
      const startDate = dayjs().format('YYYY-MM-DD');
      const endDate = dayjs().add(3, 'day').format('YYYY-MM-DD');
      return adminAppointmentsApi.getAppointments(activeBusinessId!, startDate, endDate);
    },
    enabled: !!activeBusinessId,
    refetchInterval: 60_000,
  });

  const dismissMutation = useMutation({
    mutationFn: ({ alertId }: { alertId: string }) =>
      adminAlertsApi.dismissAlert(activeBusinessId!, alertId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-alerts', activeBusinessId] });
      showSuccess(t('admin:assistantFeed.toasts.alertDismissed'));
    },
    onError: (error: Error) =>
      showError(error.message || t('admin:assistantFeed.errors.dismissAlert')),
  });

  const SNOOZE_DAYS = 7;

  const snoozeMutation = useMutation({
    mutationFn: ({ alertId }: { alertId: string }) =>
      adminAlertsApi.snoozeAlert(activeBusinessId!, alertId, { snoozeDays: SNOOZE_DAYS }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['business-alerts', activeBusinessId] });
      showSuccess(t('admin:assistantFeed.toasts.alertSnoozed', { count: SNOOZE_DAYS }));
    },
    onError: (error: Error) =>
      showError(error.message || t('admin:assistantFeed.errors.snoozeAlert')),
  });

  const rejectSuggestionMutation = useMutation({
    mutationFn: (suggestionId: string) =>
      adminSuggestionsApi.reviewSuggestion(activeBusinessId!, suggestionId, { action: 'reject' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suggestions', activeBusinessId] });
      showSuccess(t('admin:suggestionModal.toasts.rejected'));
    },
    onError: (error: Error) =>
      showError(error.message || t('admin:assistantFeed.errors.rejectSuggestion')),
  });

  const confirmAppointmentMutation = useMutation({
    mutationFn: ({ appointmentId }: { appointmentId: string }) =>
      adminAppointmentsApi.updateAppointment(activeBusinessId!, appointmentId, {
        status: 'confirmed',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['appointments-unconfirmed', activeBusinessId],
      });
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      showSuccess(t('admin:assistantFeed.toasts.markedConfirmed'));
    },
    onError: (error: Error) =>
      showError(error.message || t('admin:assistantFeed.errors.confirmAppointment')),
  });

  const requestConfirmationMutation = useMutation({
    mutationFn: ({ appointmentId }: { appointmentId: string }) =>
      adminAppointmentsApi.requestConfirmation(activeBusinessId!, appointmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['appointments-unconfirmed', activeBusinessId],
      });
      showSuccess(t('admin:assistantFeed.toasts.confirmationSent'));
    },
    onError: (error: Error) =>
      showError(error.message || t('admin:assistantFeed.errors.confirmAppointment')),
  });

  const [activeSuggestion, setActiveSuggestion] = React.useState<MessageSuggestionDTO | null>(null);

  const isBusy =
    dismissMutation.isPending ||
    snoozeMutation.isPending ||
    rejectSuggestionMutation.isPending ||
    confirmAppointmentMutation.isPending ||
    requestConfirmationMutation.isPending;
  const alerts = alertsQuery.data?.alerts ?? [];

  const alertItems: FeedItem[] = alerts.map((alert): FeedItem => {
    // For churn_risk alerts we have a specific client subject — personalize the
    // title with the client's name and deep-link the CTA to their conversation
    // so the user can actually take action instead of just being notified.
    if (alert.type === 'churn_risk' && alert.subjectName) {
      const isVip = alert.isVip === true;
      const titleKey = isVip
        ? 'admin:assistantFeed.alertTitles.churn_risk_client_vip'
        : 'admin:assistantFeed.alertTitles.churn_risk_client';
      return {
        id: alert.id,
        source: 'alert',
        type: alert.type,
        severity: alert.severity,
        title: t(titleKey, { name: alert.subjectName }),
        description: t('admin:assistantFeed.alertDescriptions.churn_risk_client'),
        timestamp: alert.firedAt,
        ctaLabel: t('admin:assistantFeed.alertCtas.churn_risk_client'),
        ctaAction: () => navigate(`/admin/messages/${alert.subjectId}`),
        dismissAction: async () => {
          await dismissMutation.mutateAsync({ alertId: alert.id });
        },
        snoozeAction: async () => {
          await snoozeMutation.mutateAsync({ alertId: alert.id });
        },
        raw: alert,
      };
    }
    return {
      id: alert.id,
      source: 'alert',
      type: alert.type,
      severity: alert.severity,
      title: getAlertTitle(alert.type, t),
      description: getAlertDescription(alert.type, t),
      timestamp: alert.firedAt,
      ctaLabel: getAlertCtaLabel(alert.type, t),
      ctaAction: () => navigate(getAlertCtaPath(alert.type)),
      dismissAction: async () => {
        await dismissMutation.mutateAsync({ alertId: alert.id });
      },
      snoozeAction: async () => {
        await snoozeMutation.mutateAsync({ alertId: alert.id });
      },
      raw: alert,
    };
  });

  // Conversations where the client is waiting for a reply
  const actionableConversations = conversationsQuery.data?.conversations.filter(needsReply) ?? [];
  const messageItems: FeedItem[] = actionableConversations.map((conv) =>
    mapConversationToFeedItem(conv, navigate, t)
  );

  const unmatchedItems: FeedItem[] = (unmatchedQuery.data?.messages ?? []).map((msg) =>
    mapUnmatchedToFeedItem(msg, navigate, t)
  );

  // Lapsed clients not already covered by a dedicated BusinessAlert
  const alertedClientIds = new Set(
    alerts.filter((a) => a.type === 'churn_risk').map((a) => a.subjectId)
  );
  const lapsedItems: FeedItem[] = (lapsedClientsQuery.data?.clients ?? [])
    .filter((c) => !alertedClientIds.has(c.id))
    .map((c) => mapLapsedClientToFeedItem(c, navigate, t));

  const suggestionItems: FeedItem[] = (suggestionsQuery.data?.suggestions ?? []).map((s) =>
    mapSuggestionToFeedItem(
      s,
      (suggestion) => setActiveSuggestion(suggestion),
      async (id) => {
        await rejectSuggestionMutation.mutateAsync(id);
      },
      t
    )
  );

  const unconfirmedAppointmentItems: FeedItem[] = (
    unconfirmedAppointmentsQuery.data?.appointments ?? []
  )
    .filter(
      (appointment) =>
        appointment.status === 'scheduled' && dayjs(appointment.startTime).isAfter(dayjs())
    )
    .map((appointment) => {
      const item = mapUnconfirmedAppointmentToFeedItem(appointment, navigate, t, (apptId) => {
        requestConfirmationMutation.mutate({ appointmentId: apptId });
      });
      item.dismissAction = async () => {
        await confirmAppointmentMutation.mutateAsync({ appointmentId: appointment.id });
      };
      return item;
    });

  // Combine all sources and sort newest first
  const items: FeedItem[] = dedupeFeedItems([
    ...alertItems,
    ...messageItems,
    ...unmatchedItems,
    ...lapsedItems,
    ...suggestionItems,
    ...unconfirmedAppointmentItems,
  ]);

  const isLoading =
    alertsQuery.isLoading ||
    conversationsQuery.isLoading ||
    unmatchedQuery.isLoading ||
    lapsedClientsQuery.isLoading ||
    suggestionsQuery.isLoading ||
    unconfirmedAppointmentsQuery.isLoading;

  const isRefetching =
    alertsQuery.isRefetching ||
    conversationsQuery.isRefetching ||
    unmatchedQuery.isRefetching ||
    lapsedClientsQuery.isRefetching ||
    suggestionsQuery.isRefetching ||
    unconfirmedAppointmentsQuery.isRefetching;

  const refetchAll = () => {
    void alertsQuery.refetch();
    void conversationsQuery.refetch();
    void unmatchedQuery.refetch();
    void lapsedClientsQuery.refetch();
    void suggestionsQuery.refetch();
    void unconfirmedAppointmentsQuery.refetch();
  };

  return {
    items,
    isLoading,
    isEmpty: !isLoading && items.length === 0,
    unactionedCount: items.length,
    refetch: refetchAll,
    isRefetching,
    isBusy,
    activeSuggestion,
    setActiveSuggestion,
  };
}
